import axios from "axios";

// Base URL for your backend API
export const API_BASE_URL = "http://40.40.69.79:3051";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});
api.interceptors.request.use(
  async (config) => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper function to get auth headers for fetch requests
export const getAuthHeaders = async () => {
  const headers = {
    "Content-Type": "application/json",
  };

  const token = sessionStorage.getItem("authToken");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

// ===================================
// 🔗 PRODUCT REVIEW API FUNCTIONS
// ===================================

// Get all reviews for a specific product variant
export const getProductReviews = async (productVariantId) => {
  try {
    const response = await api.post(`/api/v1/seritonex/reviews/by-variant`, {
      product_variant_id: productVariantId,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching product reviews:", error);
    throw error;
  }
};

// Get reviews by user ID
export const getReviewsByUser = async (userId) => {
  try {
    const response = await api.post(`/api/v1/seritonex/reviews/by-user`, {
      user_id: userId,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching user reviews:", error);
    throw error;
  }
};

// Get all reviews with status filter
export const getAllReviews = async (status = "All") => {
  try {
    const response = await api.post(`/api/v1/seritonex/reviews/all`, {
      status: status,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching all reviews:", error);
    throw error;
  }
};

// Submit a new review for a product
export const submitProductReview = async (reviewData) => {
  try {
    // Resolve user_id robustly: prefer explicit value, then sessionStorage
    let resolvedUserId = reviewData.user_id ?? null;
    try {
      if (!resolvedUserId) {
        const stored = sessionStorage.getItem("userId");
        if (stored) resolvedUserId = stored;
      }
    } catch {}
    
    // If the resolved id looks numeric, convert to Number, otherwise keep as string
    let userIdPayload = resolvedUserId;
    if (typeof userIdPayload === "string" && /^[0-9]+$/.test(userIdPayload)) {
      userIdPayload = Number(userIdPayload);
    }

    // Ensure product_variant_id and user_id are numeric as required by backend
    const productVariantIdNum = Number(reviewData.product_variant_id);

    // If userIdPayload is not numeric, try to resolve API numeric user_id via email lookup
    let finalUserId = userIdPayload;
    if (
      finalUserId === undefined ||
      finalUserId === null ||
      (typeof finalUserId === "string" && !/^[0-9]+$/.test(finalUserId))
    ) {
      try {
        const possibleEmail = sessionStorage.getItem("userEmail");
        if (possibleEmail) {
          const usersResp = await api.get(`/api/v1/seritonex/user/all`);
          const users = Array.isArray(usersResp.data) ? usersResp.data : [];
          const found = users.find(
            (u) =>
              String(u.email || u.email_address || "").toLowerCase() ===
              String(possibleEmail).toLowerCase()
          );
          if (found) {
            finalUserId = found.user_id || found.id || found.userId;
          }
        }
      } catch (e) {
        console.warn(
          "Could not resolve API user_id via email lookup:",
          e?.message || e
        );
      }
    }

    // Convert finalUserId to number if it's numeric-like
    if (typeof finalUserId === "string" && /^[0-9]+$/.test(finalUserId)) {
      finalUserId = Number(finalUserId);
    }

    const cleanPayload = {
      product_variant_id: Number(productVariantIdNum),
      user_id: typeof finalUserId === "number" ? finalUserId : finalUserId,
      rating: Number(reviewData.rating),
      detail: String(reviewData.detail || "").trim(),
      flagged: reviewData.flagged || "No",
    };

    // Validate numeric requirements before sending
    if (!Number.isFinite(cleanPayload.product_variant_id)) {
      throw new Error("Invalid product_variant_id; must be a number");
    }
    if (!Number.isFinite(Number(cleanPayload.user_id))) {
      throw new Error(
        "Invalid user_id; could not resolve a numeric API user id. Please ensure your account is linked to the site API."
      );
    }

    const response = await api.post("/api/v1/seritonex/reviews/create", cleanPayload);
    return response.data;
  } catch (error) {
    console.error("Error submitting review:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

// Get reviews by product variant
export const getReviewsByVariant = async (productVariantId) => {
  try {
    const response = await api.post("/api/v1/seritonex/reviews/by-variant", {
      product_variant_id: Number(productVariantId),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching reviews by variant:", error);
    throw error;
  }
};

// Flag a review (report, unflag, or delete)
export const flagProductReview = async (productReviewId, flagged = "Yes") => {
  try {
    const cleanPayload = {
      product_review_id: Number(productReviewId),
      flagged: flagged, // "Yes", "No", or "Deleted"
    };

    const response = await api.post("/api/v1/seritonex/reviews/flag", cleanPayload);
    return response.data;
  } catch (error) {
    console.error("Error flagging review:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

// Legacy functions for backward compatibility
export const updateProductReview = async (reviewId, reviewData) => {
  // Map to flag endpoint if updating flag status
  if (reviewData.flagged !== undefined) {
    return flagProductReview(reviewId, reviewData.flagged);
  }
  console.warn("updateProductReview: Use flagProductReview instead");
  throw new Error(
    "Review updates not supported. Use flagProductReview to flag/unflag reviews."
  );
};

export const deleteProductReview = async (reviewId) => {
  // Map to flag endpoint with "Deleted" status
  return flagProductReview(reviewId, "Deleted");
};

// Mark a review as helpful (if this endpoint exists)
export const markReviewHelpful = async (reviewId) => {
  try {
    const response = await api.post(`/api/v1/seritonex/reviews/${reviewId}/helpful`);
    return response.data;
  } catch (error) {
    console.error("Error marking review as helpful:", error);
    throw error;
  }
};

// Get reviews for admin management (legacy admin endpoint - may not exist)
export const getAllReviewsAdmin = async (
  page = 1,
  limit = 20,
  statusFilter = "All"
) => {
  try {
    // Map admin filter to API status values (backend accepts: All, Flagged, Deleted)
    let apiStatus = "All";
    if (statusFilter === "pending") apiStatus = "Flagged"; // Flagged reviews
    else if (statusFilter === "rejected")
      apiStatus = "Deleted"; // Deleted reviews
    else apiStatus = "All"; // All reviews (includes approved)

    // Fetch reviews from the API
    const response = await api.post(`/api/v1/seritonex/reviews/all`, {
      status: apiStatus,
    });

    // The API returns array, wrap it in pagination structure for compatibility
    const reviews = Array.isArray(response.data) ? response.data : [];

    // Filter for "approved" on the frontend since backend doesn't have that filter
    let filteredReviews = reviews;
    if (statusFilter === "approved") {
      filteredReviews = reviews.filter((r) => r.flagged === "No");
    }

    return {
      reviews: filteredReviews,
      totalPages: Math.ceil(filteredReviews.length / limit),
      currentPage: page,
      total: filteredReviews.length,
    };
  } catch (error) {
    console.error("Error fetching all reviews:", error);
    console.error("Error details:", error.response?.data);
    throw error;
  }
};

// Moderate a review (uses the flag endpoint)
export const moderateReview = async (reviewId, action, reason = "") => {
  try {
    // Map admin actions to flag status
    let flagStatus = "No";
    if (action === "approved" || action === "approve" || action === "unflag") {
      flagStatus = "No"; // Approved
    } else if (
      action === "rejected" ||
      action === "reject" ||
      action === "delete" ||
      action === "deleted"
    ) {
      flagStatus = "Deleted"; // Rejected/Deleted
    } else if (action === "flag" || action === "pending") {
      flagStatus = "Yes"; // Flagged/Pending
    }

    return await flagProductReview(reviewId, flagStatus);
  } catch (error) {
    console.error("Error moderating review:", error);
    throw error;
  }
};

// Get review statistics for admin dashboard
export const getReviewStats = async () => {
  try {
    // Fetch all reviews and calculate stats
    const response = await api.post(`/api/v1/seritonex/reviews/all`, { status: "All" });
    const reviews = Array.isArray(response.data) ? response.data : [];

    // Map flagged status:
    // "Yes" = flagged/reported (pending moderation)
    // "No" = normal review (approved)
    // "Deleted" = rejected
    // "Approved" = explicitly approved
    const mapStatus = (review) => {
      const flagged = String(review.flagged || "").trim();

      if (flagged === "Yes") return "pending"; // Flagged/reported reviews
      if (flagged === "Deleted") return "rejected";
      if (flagged === "Approved" || flagged === "No") return "approved"; // Normal reviews or explicitly approved

      return "approved"; // Default to approved
    };

    const pending = reviews.filter((r) => mapStatus(r) === "pending");
    const approved = reviews.filter((r) => mapStatus(r) === "approved");
    const rejected = reviews.filter((r) => mapStatus(r) === "rejected");

    const stats = {
      total: reviews.length,
      pending: pending.length,
      approved: approved.length,
      rejected: rejected.length,
      averageRating:
        reviews.length > 0
          ? reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) /
            reviews.length
          : 0,
    };

    return stats;
  } catch (error) {
    console.error("Error fetching review stats:", error);
    throw error;
  }
};

export default api;

// Get all products
export const getAllProducts = async () => {
  try {
    const response = await api.get('/api/v1/seritonex/products/all');
    return response.data;
  } catch (error) {
    console.error("Error fetching all products:", error);
    throw error;
  }
};

// Get product by ID
export const getProductById = async (productId) => {
  try {
    const response = await api.post('/api/v1/seritonex/products/by-id', {
      product_id: productId,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching product by ID:", error);
    throw error;
  }
};


// Get products by category relationship
export const getProductsByCategory = async (categoryRelationshipId) => {
  try {
    const response = await api.post('/api/v1/seritonex/products/by-category-relationship', {
      category_relationship_id: categoryRelationshipId,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching products by category:", error);
    throw error;
  }
};

// Search products
export const searchProducts = async (searchTerm) => {
  try {
    const response = await api.post('/api/v1/seritonex/products/find', {
      search_term: searchTerm,
    });
    return response.data;
  } catch (error) {
    console.error("Error searching products:", error);
    throw error;
  }
};

// Fetch product categories from backend
export const getCategories = async () => {
  try {
    // Backend exposes categories at /category/all (confirmed in AdminCategories.js)
    const token = sessionStorage.getItem("authToken") || "";
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await api.get(`/api/v1/seritonex/category/all`, { headers });
    return response.data;
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
};

// Fetch root-level categories for navigation
export const fetchRoots = async () => {
  try {
    const response = await api.get(`/api/v1/seritonex/category/roots`);
    return response.data;
  } catch (error) {
    console.error("Error fetching root categories:", error);
    throw error;
  }
};

// Fetch categories for a given classification ID
export const fetchCategoriesByClassification = async (classificationId) => {
  try {
    const response = await api.post(`/api/v1/seritonex/category/by-classification-id`, {
      classification_id: Number(classificationId),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching categories by classification:", error);
    throw error;
  }
};

// Fetch sub-categories for a given parent relationship ID
export const fetchSubCategories = async (parent_relationship_id) => {
  try {
    const response = await api.post(`/api/v1/seritonex/category/parent-sub-categories`, {
      parent_relationship_id,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sub-categories:", error);
    throw error;
  }
};

// Fetch parent category for a given category relationship ID
export const fetchParentCategory = async (category_relationship_id) => {
  try {
    const response = await api.post(`/api/v1/seritonex/category/parent-category`, {
      category_relationship_id,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching parent category:", error);
    throw error;
  }
};

// Submit a contact form
export const submitContactForm = async (form) => {
  try {
    const response = await api.post(`/contact`, form);
    return response.data;
  } catch (error) {
    console.error("Error submitting contact form:", error);
    throw error;
  }
};

// -----------------------------
// Announcement API
// Backend endpoints:
// POST /announcement/current  -> returns current active announcement (no body)
// POST /announcement/all      -> list all announcements (accepts { include_deleted: true })
// POST /announcement/create   -> create new announcement
// POST /announcement/edit     -> edit announcement
// POST /announcement/delete   -> delete announcement
// -----------------------------

export const getCurrentAnnouncement = async () => {
  try {
    const token = sessionStorage.getItem("authToken") || "";
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await api.post(`/api/v1/seritonex/announcement/current`, {}, { headers });
    return response.data;
  } catch (error) {
    console.error("Error fetching current announcement:", error);
    throw error;
  }
};

export const getAllAnnouncements = async (
  opts = { include_deleted: false }
) => {
  try {
    const token = sessionStorage.getItem("authToken") || "";
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await api.post(`/api/v1/seritonex/announcement/all`, opts, { headers });
    return response.data;
  } catch (error) {
    console.error("Error fetching all announcements:", error);
    throw error;
  }
};

export const createAnnouncement = async (payload) => {
  try {
    // Convert datetime-local format (YYYY-MM-DDTHH:mm) to backend format (YYYY-MM-DD HH:MM:SS)
    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      // If already in correct format, return as-is
      if (dateStr.includes(" ")) return dateStr;
      // Convert YYYY-MM-DDTHH:mm to YYYY-MM-DD HH:MM:SS
      return dateStr.replace("T", " ") + ":00";
    };

    // Clean payload - remove any undefined or empty values
    const cleanPayload = {
      title: String(payload.title || "").trim(),
      msg: String(payload.msg || "").trim(),
      start_date: formatDate(String(payload.start_date || "").trim()),
      end_date: formatDate(String(payload.end_date || "").trim()),
    };

    // Validate required fields
    if (!cleanPayload.title) throw new Error("Title is required");
    if (!cleanPayload.msg) throw new Error("Message is required");
    if (!cleanPayload.start_date) throw new Error("Start date is required");
    if (!cleanPayload.end_date) throw new Error("End date is required");

    // Use axios directly with full URL to bypass potential interceptor issues
    const token = sessionStorage.getItem("authToken") || "";
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/seritonex/announcement/create`,
      cleanPayload,
      {
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        },
        timeout: 15000,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error creating announcement:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

export const editAnnouncement = async (payload) => {
  try {
    // Convert datetime-local format (YYYY-MM-DDTHH:mm) to backend format (YYYY-MM-DD HH:MM:SS)
    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      // If already in correct format, return as-is
      if (dateStr.includes(" ")) return dateStr;
      // Convert YYYY-MM-DDTHH:mm to YYYY-MM-DD HH:MM:SS
      return dateStr.replace("T", " ") + ":00";
    };

    // Clean payload for editing
    const cleanPayload = {
      announcement_id: Number(payload.announcement_id),
      title: String(payload.title || "").trim(),
      msg: String(payload.msg || "").trim(),
      start_date: formatDate(String(payload.start_date || "").trim()),
      end_date: formatDate(String(payload.end_date || "").trim()),
    };

    // Validate required fields
    if (!cleanPayload.announcement_id)
      throw new Error("Announcement ID is required");
    if (!cleanPayload.title) throw new Error("Title is required");
    if (!cleanPayload.msg) throw new Error("Message is required");
    if (!cleanPayload.start_date) throw new Error("Start date is required");
    if (!cleanPayload.end_date) throw new Error("End date is required");

    // Use axios directly with full URL
    const token = sessionStorage.getItem("authToken") || "";
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/seritonex/announcement/edit`,
      cleanPayload,
      {
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        },
        timeout: 15000,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error editing announcement:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

export const deleteAnnouncement = async (payload) => {
  try {
    const token = sessionStorage.getItem("authToken") || "";
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await api.post(`${API_BASE_URL}/api/v1/seritonex/announcement/delete`, payload, { headers });
    return response.data;
  } catch (error) {
    console.error("Error deleting announcement:", error);
    throw error;
  }
};
// ===================================
// 📦 ORDERS API FUNCTIONS
// ===================================

// Get order by ID
export const getOrderById = async (salesOrderId) => {
  try {
    const token = sessionStorage.getItem("authToken") || "";
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await api.post(
      `${API_BASE_URL}/api/v1/seritonex/orders/by-id`, 
      { sales_order_id: parseInt(salesOrderId) }, 
      { headers }
    );
    return Array.isArray(response.data) ? response.data[0] : response.data;
  } catch (error) {
    console.error("Error fetching order by ID:", error);
    throw error;
  }
};

// Get the latest order for a given user (fallback used by ThankYou page)
export const getLatestOrderForUser = async (userId) => {
  try {
    const token = sessionStorage.getItem("authToken") || "";
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Prefer the by-user endpoint which should return an array of orders
    const payload = {};
    if (userId !== undefined && userId !== null) {
      // If numeric-like, send as number
      const maybeNum = String(userId).trim();
      payload.user_id = /^[0-9]+$/.test(maybeNum) ? Number(maybeNum) : maybeNum;
    }

    let response = await api.post(`${API_BASE_URL}/api/v1/seritonex/orders/by-user`, payload, { headers });
    let orders = Array.isArray(response.data) ? response.data : [];

    if ((!orders || orders.length === 0)) {
      try {
        const alt = await api.post(`${API_BASE_URL}/api/v1/seritonex/orders/latest`, payload, { headers });
        orders = Array.isArray(alt.data) ? alt.data : alt.data ? [alt.data] : [];
      } catch (e) {
        // ignore alternative endpoint failure
      }
    }

    if (!orders || orders.length === 0) return null;

    // Pick the most recent order by created_at or date when available
    orders.sort((a, b) => {
      const ta = new Date(a.created_at || a.date || 0).getTime() || 0;
      const tb = new Date(b.created_at || b.date || 0).getTime() || 0;
      return tb - ta;
    });

    return orders[0];
  } catch (error) {
    console.error("Error fetching latest order for user:", error);
    throw error;
  }
};

// ===================================
// 🖥️ PC BUILDER API FUNCTIONS
// ===================================

// Fetch PC builder categories from backend
export const getPCBuilderCategories = async () => {
  try {
    const response = await api.get("/api/v1/seritonex/pc-builder/categories");
    return response.data;
  } catch (error) {
    console.error("Error fetching PC builder categories:", error);
    throw error;
  }
};

// Fetch product specs for a specific category
export const getProductSpecsByCategory = async (categoryId) => {
  try {
    const response = await api.post("/api/v1/seritonex/pc-builder/specs-by-category", {
      category_id: categoryId,
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching specs for category ${categoryId}:`, error);
    throw error;
  }
};

// Fetch all product specs for PC builder
export const getAllPCBuilderSpecs = async () => {
  try {
    const response = await api.get("/api/v1/seritonex/pc-builder/all-specs");
    return response.data;
  } catch (error) {
    console.error("Error fetching all PC builder specs:", error);
    throw error;
  }
};

// ===================================
// 🧩 Category Specification API
// ===================================

// Create a new category specification link
export const createCategorySpecification = async (
  category_id,
  specification_id,
  required = false
) => {
  try {
    const payload = {
      category_id: Number(category_id),
      specification_id: Number(specification_id),
      required: required ? 1 : 0,
    };
    const response = await api.post(
      "/api/v1/seritonex/category-specification/create",
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error creating category specification:", error);
    throw error;
  }
};

// Get all specifications for a category
export const getCategorySpecifications = async (category_id) => {
  try {
    const response = await api.post(
      "/api/v1/seritonex/category-specification/by-category",
      { category_id: Number(category_id) }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching category specifications:", error);
    throw error;
  }
};

// Get a single category_specification by id
export const getCategorySpecificationById = async (
  category_specification_id
) => {
  try {
    const response = await api.post(
      "/api/v1/seritonex/category-specification/by-id",
      { category_specification_id: Number(category_specification_id) }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching category specification by ID:", error);
    throw error;
  }
};

// Update an existing category specification
export const updateCategorySpecification = async (
  category_specification_id,
  category_id,
  specification_id,
  required
) => {
  try {
    const payload = {
      category_specification_id: Number(category_specification_id),
      category_id: Number(category_id),
      specification_id: Number(specification_id),
      required: required ? 1 : 0,
    };
    const response = await api.post(
      "/api/v1/seritonex/category-specification/edit",
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating category specification:", error);
    throw error;
  }
};

// Delete a category specification
export const deleteCategorySpecification = async (category_specification_id) => {
  try {
    const response = await api.post(
      "/api/v1/seritonex/category-specification/delete",
      { category_specification_id: Number(category_specification_id) }
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting category specification:", error);
    throw error;
  }
};